import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import ConfirmDialog from "components/common/ConfirmDialog";
import CustomScrollbars from "components/CustomScrollbars";
import {
  useDeleteAccessGroupMutation,
  useGetAccessCatalogQuery,
  useGetAccessGroupsQuery,
} from "features/accessGroups/accessGroupsApi";
import { useAppDispatch } from "features/store";
import { pushNotification } from "features/notificationSlice";
import type { AccessGroup, AccessGroupBlocker } from "types/interfaces/access";
import { MANAGEMENT_TABLE_AREA_SX } from "components/management/managementTheme";
import AccessGroupDialog from "./AccessGroupDialog";
import { grantedLines } from "./permissionsPanelModel";

export default function AccessGroupsTab() {
  const dispatch = useAppDispatch();
  const { data: catalog } = useGetAccessCatalogQuery();
  const { data: groups, isLoading } = useGetAccessGroupsQuery();
  const [deleteGroup, deleteState] = useDeleteAccessGroupMutation();

  const [editing, setEditing] = useState<AccessGroup | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<AccessGroup | null>(null);
  const [blockers, setBlockers] = useState<AccessGroupBlocker[] | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (group: AccessGroup) => {
    setEditing(group);
    setDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteGroup(deleting.id).unwrap();
      dispatch(
        pushNotification({
          message: `Deleted ${deleting.name}.`,
          severity: "success",
        }),
      );
      setDeleting(null);
    } catch (e) {
      const data = (
        e as { data?: { error?: string; members?: AccessGroupBlocker[] } }
      )?.data;
      if (data?.members?.length) {
        setBlockers(data.members);
      } else {
        dispatch(
          pushNotification({
            message: data?.error ?? "Could not delete the group.",
            severity: "error",
          }),
        );
        setDeleting(null);
      }
    }
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            A group sets what its members reach on each page. Changes apply on
            their next request; an internal user without a group reaches no
            page.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreate}
          disabled={!catalog}
        >
          New Group
        </Button>
      </Box>

      <Box sx={MANAGEMENT_TABLE_AREA_SX}>
        <CustomScrollbars sx={{ flex: 1, minHeight: 0 }} trackOffsetTop={40}>
          <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Pages</TableCell>
                <TableCell>Applies to</TableCell>
                <TableCell align="right">Members</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {(groups ?? []).map((group) => (
                <TableRow key={group.id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {group.name}
                      </Typography>
                      {group.is_system && (
                        <Chip size="small" label="System" variant="outlined" />
                      )}
                    </Box>
                    {group.description && (
                      <Typography variant="caption" color="text.secondary">
                        {group.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {catalog
                      ? grantedLines(catalog, group.tiers)
                          .map((line) => line.name)
                          .join(", ") || "-"
                      : "-"}
                  </TableCell>
                  <TableCell>{group.applies_to}</TableCell>
                  <TableCell align="right">{group.member_count}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      onClick={() => openEdit(group)}
                      disabled={!catalog}
                    >
                      Edit
                    </Button>
                    {!group.is_system && (
                      <Button
                        size="small"
                        color="error"
                        sx={{ ml: 1 }}
                        onClick={() => {
                          setBlockers(null);
                          setDeleting(group);
                        }}
                      >
                        Delete
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CustomScrollbars>
      </Box>

      {catalog && (
        <AccessGroupDialog
          open={dialogOpen}
          catalog={catalog}
          group={editing}
          onClose={() => setDialogOpen(false)}
          onSaved={(saved) => {
            setDialogOpen(false);
            dispatch(
              pushNotification({
                message: `Saved ${saved.name}.`,
                severity: "success",
              }),
            );
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title={
          blockers ? "Group still has members" : "Delete internal access group"
        }
        message={
          blockers ? (
            <Alert severity="warning">
              Move these users to another group first:
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {blockers.map((b) => (
                  <li key={b.id}>
                    {b.display_name
                      ? `${b.display_name} (${b.username})`
                      : b.username}
                  </li>
                ))}
              </Box>
            </Alert>
          ) : (
            `Delete ${deleting?.name ?? "this group"}? This cannot be undone.`
          )
        }
        confirmLabel={blockers ? "Close" : "Delete"}
        loading={deleteState.isLoading}
        onConfirm={() => {
          if (blockers) {
            setDeleting(null);
            setBlockers(null);
          } else {
            void confirmDelete();
          }
        }}
        onCancel={() => {
          setDeleting(null);
          setBlockers(null);
        }}
      />
    </Box>
  );
}
